import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Check, X } from 'lucide-react';

interface EditableTitleProps {
  title: string;
  onSave: (newTitle: string) => Promise<void>;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  title,
  onSave,
  className = '',
  placeholder = 'Enter title...',
  autoFocus = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Auto-enter edit mode on mount for new/untitled items
  useEffect(() => {
    if (autoFocus) {
      setIsEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  const handleSave = async () => {
    if (editedTitle.trim() === '') {
      setEditedTitle(title);
      setIsEditing(false);
      return;
    }

    if (editedTitle === title) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editedTitle);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save title:', error);
      setEditedTitle(title);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2">
        <input
          ref={inputRef}
          type="text"
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          placeholder={placeholder}
          disabled={isSaving}
          className={`${className} border-2 border-primary-500 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-medical-700 dark:text-medical-100 dark:border-primary-400`}
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
          title="Save"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 group">
      <h1 className={className}>{title}</h1>
      <button
        onClick={() => setIsEditing(true)}
        className="opacity-0 group-hover:opacity-100 p-1 text-medical-400 hover:text-primary-600 dark:text-medical-500 dark:hover:text-primary-400 hover:bg-medical-50 dark:hover:bg-medical-700 rounded transition-all"
        title="Edit title"
      >
        <Pencil className="h-4 w-4" />
      </button>
    </div>
  );
};
